import React from 'react';
import EllipsisHover from '@/pages/aigc/components/EllipsisHover';

interface HighlightTextProps {
  /** 要显示的文本 */
  name: string;
  /** 搜索关键词 */
  keyword: string;
  /** 是否启用溢出省略 + Hover 显示完整文本 tips（内部复用 EllipsisHover） */
  ellipsis?: boolean;
  /** 外层容器类名（仅 ellipsis=true 时生效） */
  className?: string;
  /** 外层容器行内样式（仅 ellipsis=true 时生效） */
  style?: React.CSSProperties;
  /** 最大行数，仅 ellipsis=true 时生效，默认 1 */
  maxLines?: number;
  /** Bubble 弹层类名（仅 ellipsis=true 时生效） */
  overlayClassName?: string;
}

const escapeRegExp = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** 按关键词把文本切成片段并给命中片段加高亮色 */
const renderHighlightedParts = (name: string, keyword: string): React.ReactNode => {
  if (!keyword) return <>{name}</>;
  const regex = new RegExp(`(${escapeRegExp(keyword)})`, 'gi');
  const parts = name?.split(regex) || [];

  return (
    <>
      {parts.map((part: string, index: number) => {
        if (part?.toLowerCase() === keyword?.toLowerCase()) {
          return <span key={`${index}-${part}`} style={{ color: '#3C7BFF' }}>{part}</span>;
        }
        return <span key={`${index}-${part}`}>{part}</span>;
      })}
    </>
  );
};

/**
 * 高亮文本组件 - 在文本中高亮显示搜索关键词（不区分大小写）
 * - 默认仅渲染高亮文本，完全向后兼容
 * - `ellipsis=true` 时自动套一层 EllipsisHover，超出宽度显示 tips
 * @param name - 要显示的文本
 * @param keyword - 搜索关键词
 * @param ellipsis - 是否启用 tips
 */
export const HighlightText = React.memo(({
  name,
  keyword,
  ellipsis,
  className,
  style,
  maxLines,
  overlayClassName,
}: HighlightTextProps) => {
  const highlighted = renderHighlightedParts(name, keyword);

  if (!ellipsis) return <>{highlighted}</>;

  return (
    <EllipsisHover
      text={name}
      className={className}
      style={style}
      maxLines={maxLines}
      overlayClassName={overlayClassName}
    >
      {highlighted}
    </EllipsisHover>
  );
});

HighlightText.displayName = 'HighlightText';

export default HighlightText;
